import {
  Box3,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  EdgesGeometry,
  Euler,
  Group,
  LineBasicMaterial,
  LineSegments,
  MathUtils,
  Matrix4,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector2,
  Vector3
} from 'three';
import { PerFaceMaterial } from '../addons/per-face-material';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import {
  FEATURE_EDGES_MAX_TRIANGLES,
  PART_METALNESS,
  PART_ROUGHNESS,
  PART_REFLECTIVITY,
  PART_CLEARCOAT,
  PART_ENVMAP_INTENSITY
} from '../engine.constants';

/** Progress of a soft-jaws result load; `percent` is null when it can't be measured. */
export interface JawsResultProgress {
  percent: number | null;
  message: string;
}

/** Share of the reported progress given to the download, the rest to geometry. */
const DOWNLOAD_PROGRESS_SHARE = 70;

/** Resolve after the browser has had a chance to paint. */
function nextPaint(): Promise<void> {
  return new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
}

/**
 * Download a result proto, reporting 0-`share` percent as the bytes arrive.
 * Reports null while the response carries no Content-Length to measure against.
 */
export function fetchResultProto(
  http: HttpClient,
  url: string,
  share: number,
  onProgress: (percent: number | null) => void,
): Promise<ArrayBuffer> {
  const request = new HttpRequest('GET', url, { responseType: 'arraybuffer', reportProgress: true });
  return new Promise<ArrayBuffer>((resolve, reject) => {
    http.request<ArrayBuffer>(request).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.DownloadProgress) {
          onProgress(event.total ? Math.round((event.loaded / event.total) * share) : null);
        } else if (event.type === HttpEventType.Response) {
          resolve(event.body as ArrayBuffer);
        }
      },
      error: (err) => reject(err),
    });
  });
}
import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { decodeAssembly } from '../assembly-proto-decoder';
import { ProjectType } from '../../models/enums';
import { EngineContext } from './engine-context';

/**
 * Soft-jaws visualization + collision + rotation gizmo. Extracted from Engine.
 */
export class JawsManager {
  static readonly MIN_JAW_GAP = 1; // mm — never let the two jaws collide
  static readonly DEFAULT_JAW_GAP = 30; // mm

  /** Legacy `jawWall` is interpreted as a gap for pre-rename projects. */
  static resolveJawGap(config: { jawGap?: number; jawWall?: number }): number {
    if (typeof config.jawGap === 'number' && !Number.isNaN(config.jawGap)) return config.jawGap;
    if (typeof config.jawWall === 'number' && !Number.isNaN(config.jawWall)) return config.jawWall;
    return JawsManager.DEFAULT_JAW_GAP;
  }

  /** Legacy `clampingDepth` becomes `-z` (old clamping_depth=20 ⇒ z=-20). */
  static resolvePartOffset(config: {
    partOffset?: { x?: number; y?: number; z?: number };
    clampingDepth?: number;
  }): { x: number; y: number; z: number } {
    const x = config.partOffset?.x ?? 0;
    const y = config.partOffset?.y ?? 0;
    let z: number;
    if (typeof config.partOffset?.z === 'number' && !Number.isNaN(config.partOffset.z)) {
      z = config.partOffset.z;
    } else if (typeof config.clampingDepth === 'number' && !Number.isNaN(config.clampingDepth)) {
      z = -config.clampingDepth;
    } else {
      z = -20;
    }
    return { x, y, z };
  }

  private jawsAnimationTween?: Tween<{ t: number }>;
  public jawsValid = false;

  private minGapGroup?: Group;
  private minGapBadge?: HTMLDivElement;
  private minGapLineMaterial?: LineMaterial;
  private minGapVisible = false;
  private minGapMidpoint = new Vector3();
  private pulseTimeout?: ReturnType<typeof setTimeout>;

  private rotationChangeCallback?: (rotation: { x: number; y: number; z: number }) => void;

  /**
   * Invoked once when a rotation-gizmo drag ends (pointer released), so a
   * caller can commit a single update instead of one per `objectChange`
   * tick fired continuously while dragging.
   */
  onRotationDragEnd?: () => void;

  /**
   * Every model vertex expressed in pivot-local space, cached across calls.
   *
   * updateJaws() needs the bbox of the ROTATED model and runs on every form
   * change. Re-deriving it through calculateGroupMinMaxCenter() meant walking the
   * scene graph and allocating a Vector3 round-trip per vertex — on a
   * multi-million-vertex imported mesh that is seconds of main-thread work per
   * keystroke. Only the pivot's transform varies between calls, so caching the
   * points in pivot space lets each recompute be one tight numeric loop over a
   * Float32Array. Invalidated whenever parts are added or removed.
   */
  private cachedPivotLocalVertices?: Float32Array;

  constructor(private ctx: EngineContext) {}

  /** Drop the cached vertex snapshot; the next updateJaws() rebuilds it. */
  invalidateVertexCache(): void {
    this.cachedPivotLocalVertices = undefined;
  }

  private getPivotLocalVertices(): Float32Array | undefined {
    if (this.cachedPivotLocalVertices) return this.cachedPivotLocalVertices;

    this.ctx.pivot.updateWorldMatrix(true, true);
    this.ctx.group.updateWorldMatrix(true, true);
    const toPivotLocal = new Matrix4().copy(this.ctx.pivot.matrixWorld).invert();

    const meshes: Mesh[] = [];
    let total = 0;
    this.ctx.group.traverse((child) => {
      if (
        child instanceof Mesh &&
        child.isMesh &&
        child.geometry instanceof BufferGeometry &&
        ('isMergedMesh' in child.userData || 'faceId' in child.userData)
      ) {
        const position = child.geometry.attributes['position'];
        if (!position) return;
        meshes.push(child);
        total += position.count;
      }
    });

    if (total === 0) return undefined;

    const out = new Float32Array(total * 3);
    const v = new Vector3();
    const m = new Matrix4();
    let offset = 0;
    for (const child of meshes) {
      const position = child.geometry.attributes['position'];
      m.multiplyMatrices(toPivotLocal, child.matrixWorld);
      for (let i = 0; i < position.count; i++) {
        v.fromBufferAttribute(position as BufferAttribute, i).applyMatrix4(m);
        out[offset++] = v.x;
        out[offset++] = v.y;
        out[offset++] = v.z;
      }
    }

    this.cachedPivotLocalVertices = out;
    return out;
  }

  /**
   * World-space bbox of the model under the pivot's CURRENT transform, computed
   * from the cached pivot-local snapshot. Falls back to the generic scene-graph
   * walk when the cache can't be built (no geometry loaded yet).
   */
  private measureRotatedModel(): { center: Vector3; box: Box3 } {
    const local = this.getPivotLocalVertices();
    if (!local) {
      const { center, box } = this.ctx.calculateGroupMinMaxCenter(this.ctx.group);
      return { center, box };
    }

    this.ctx.pivot.updateWorldMatrix(true, true);
    const e = this.ctx.pivot.matrixWorld.elements;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (let i = 0; i < local.length; i += 3) {
      const x = local[i], y = local[i + 1], z = local[i + 2];
      const wx = e[0] * x + e[4] * y + e[8] * z + e[12];
      const wy = e[1] * x + e[5] * y + e[9] * z + e[13];
      const wz = e[2] * x + e[6] * y + e[10] * z + e[14];
      if (wx < minX) minX = wx;
      if (wy < minY) minY = wy;
      if (wz < minZ) minZ = wz;
      if (wx > maxX) maxX = wx;
      if (wy > maxY) maxY = wy;
      if (wz > maxZ) maxZ = wz;
    }

    const box = new Box3(new Vector3(minX, minY, minZ), new Vector3(maxX, maxY, maxZ));
    const center = new Vector3(
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2
    );
    return { center, box };
  }

  initJaws(): void {
    const config = this.ctx.session.project.softJaws;
    if (!config) return;

    if (this.ctx.jaws.fixed.mesh) {
      this.updateJaws();
      return;
    }

    const boxGeometry = new BoxGeometry(1, 1, 1);
    this.ctx.jaws.fixed.mesh = new Mesh(
      boxGeometry,
      new MeshStandardMaterial({ color: 0xe74c3c, opacity: 0.2, transparent: true })
    );
    this.ctx.jaws.fixed.mesh.renderOrder = 1;
    this.ctx.jaws.fixed.edges = new LineSegments(
      new EdgesGeometry(boxGeometry),
      new LineBasicMaterial({ color: 0xc0392b, linewidth: 1 })
    );

    this.ctx.jaws.movable.mesh = new Mesh(
      boxGeometry,
      new MeshStandardMaterial({ color: 0xe74c3c, opacity: 0.2, transparent: true })
    );
    this.ctx.jaws.movable.mesh.renderOrder = 1;
    this.ctx.jaws.movable.edges = new LineSegments(
      new EdgesGeometry(boxGeometry),
      new LineBasicMaterial({ color: 0xc0392b, linewidth: 1 })
    );

    this.ctx.jaws.fixed.mesh.geometry.computeBoundingBox();
    this.ctx.jaws.movable.mesh.geometry.computeBoundingBox();
    this.ctx.jaws.fixed.mesh.geometry.computeBoundsTree();
    this.ctx.jaws.movable.mesh.geometry.computeBoundsTree();

    // Add jaws and edges to group
    this.ctx.jaws.group.add(this.ctx.jaws.fixed.mesh);
    this.ctx.jaws.group.add(this.ctx.jaws.fixed.edges);
    this.ctx.jaws.group.add(this.ctx.jaws.movable.mesh);
    this.ctx.jaws.group.add(this.ctx.jaws.movable.edges);
    this.ctx.scene.add(this.ctx.jaws.group);

    this.updateJaws();
  }

  updateJaws(): {
    effectiveJawWall: number;
    effectiveJawGap: number;
    atMinGap: boolean;
  } | undefined {
    const config = this.ctx.session.project.softJaws;
    if (!config) return undefined;

    if (this.jawsAnimationTween) {
      this.jawsAnimationTween.stop();
      this.jawsAnimationTween = undefined;
    }

    const captureState = (mesh?: Mesh | LineSegments) =>
      mesh
        ? {
            position: mesh.position.clone(),
            scale: mesh.scale.clone()
          }
        : undefined;

    const initialState = {
      pivotPosition: this.ctx.pivot.position.clone(),
      pivotQuaternion: this.ctx.pivot.quaternion.clone(),
      jawsPivotPosition: this.ctx.jaws.jawsPivot.position.clone(),
      fixed: captureState(this.ctx.jaws.fixed.mesh),
      fixedEdges: captureState(this.ctx.jaws.fixed.edges),
      movable: captureState(this.ctx.jaws.movable.mesh),
      movableEdges: captureState(this.ctx.jaws.movable.edges)
    };

    const { center } = this.ctx.objectInfo;

    const jawLength = config.length;
    const jawWidth = config.width;
    const jawHeight = config.height;
    const requestedGap = JawsManager.resolveJawGap(config);
    const partOffset = JawsManager.resolvePartOffset(config);

    this.ctx.pivot.rotation.set(0, 0, 0);
    this.ctx.pivot.position.copy(center);

    const rX = MathUtils.degToRad(config.modelRotation.x);
    const rY = MathUtils.degToRad(config.modelRotation.y);
    const rZ = MathUtils.degToRad(config.modelRotation.z);

    this.ctx.pivot.rotateOnWorldAxis(new Vector3(1, 0, 0), rX);
    this.ctx.pivot.rotateOnWorldAxis(new Vector3(0, 1, 0), rY);
    this.ctx.pivot.rotateOnWorldAxis(new Vector3(0, 0, 1), rZ);

    this.ctx.pivot.updateWorldMatrix(true, true);
    this.ctx.group.updateWorldMatrix(true, true);
    // boxChanged is the rotated-part bbox WITHOUT partOffset — jaws anchor here,
    // partOffset.x/y is applied to the pivot later so the part slides under
    // stationary jaws.
    const { center: centerChanged, box: boxChanged } = this.measureRotatedModel();

    // The runner anchors its world on the ROTATED part: ScaleRotateCenter* moves
    // the part so its rotated bbox centre lands on partOffset.x/y, then builds the
    // jaws symmetrically about the origin. We rotate about the UNROTATED bbox
    // centre, so for any rotation that is not a multiple of 90° the rotated centre
    // drifts off the pivot by `recenter`. Without subtracting it the whole preview
    // (part AND jaws) sits `recenter` away from where the result will land, so the
    // model appeared to jump when the result replaced the preview.
    const recenterX = centerChanged.x - center.x;
    const recenterY = centerChanged.y - center.y;

    // Datum the part is parked against; the jaws' base sits on it, matching the
    // binary, which builds both jaws from z = 0 upward.
    const groundZ = this.ctx.gridHelper.position.z;
    const jawCenterZ = groundZ + jawHeight / 2;

    const boxSizeX = boxChanged.max.x - boxChanged.min.x;
    // gap = boxSizeX - 2·jawWidth + 2·jawWall  ⇒  jawWall = (gap + 2·jawWidth - boxSizeX) / 2.
    // jawWall is allowed to go negative: soft jaws cradle deeper than the bbox.
    const effectiveJawGap = Math.max(requestedGap, JawsManager.MIN_JAW_GAP);
    const effectiveJawWall = (effectiveJawGap + 2 * jawWidth - boxSizeX) / 2;
    const effectiveJawWidth = jawWidth;

    // Width = X, Length = Y, Height = Z.
    this.ctx.jaws.fixed.mesh?.scale.set(effectiveJawWidth, jawLength, jawHeight);
    this.ctx.jaws.movable.mesh?.scale.set(effectiveJawWidth, jawLength, jawHeight);
    this.ctx.jaws.fixed.edges?.scale.set(effectiveJawWidth, jawLength, jawHeight);
    this.ctx.jaws.movable.edges?.scale.set(effectiveJawWidth, jawLength, jawHeight);

    this.ctx.jaws.fixed.mesh?.geometry.computeBoundingBox();
    this.ctx.jaws.movable.mesh?.geometry.computeBoundingBox();
    this.ctx.jaws.fixed.mesh?.geometry.computeBoundsTree();
    this.ctx.jaws.movable.mesh?.geometry.computeBoundsTree();

    [this.ctx.jaws.fixed.mesh, this.ctx.jaws.fixed.edges].forEach((mesh) => {
      mesh?.position.set(boxChanged.min.x - recenterX + effectiveJawWidth / 2 - effectiveJawWall, center.y, jawCenterZ);
    });

    [this.ctx.jaws.movable.mesh, this.ctx.jaws.movable.edges].forEach((mesh) => {
      mesh?.position.set(boxChanged.max.x - recenterX - effectiveJawWidth / 2 + effectiveJawWall, center.y, jawCenterZ);
    });

    // Park the part vertically so its rotated lowest vertex lands at
    // ground + jawHeight + partOffset.z (z=0 = on top of the jaws).
    this.ctx.pivot.position.z -= boxChanged.min.z - jawHeight - partOffset.z - groundZ;
    this.ctx.jaws.jawsPivot.position.z = this.ctx.pivot.position.z;

    // X/Y offset is applied AFTER the jaws are placed from boxChanged, so the
    // part slides while the jaws stay put. `recenter` puts the rotated bbox
    // centre — not the pivot — on partOffset, which is the runner's convention.
    this.ctx.pivot.position.x += partOffset.x - recenterX;
    this.ctx.pivot.position.y += partOffset.y - recenterY;

    const actualGap = boxSizeX - 2 * effectiveJawWidth + 2 * effectiveJawWall;
    const atMinGap = actualGap <= JawsManager.MIN_JAW_GAP + 0.1;
    if (atMinGap) {
      const fixedInnerX = boxChanged.min.x - recenterX + effectiveJawWidth - effectiveJawWall;
      const movableInnerX = boxChanged.max.x - recenterX - effectiveJawWidth + effectiveJawWall;
      this.showMinGapIndicator(fixedInnerX, movableInnerX, center.y, jawCenterZ);
    } else {
      this.hideMinGapIndicator();
    }

    const targetState = {
      pivotPosition: this.ctx.pivot.position.clone(),
      pivotQuaternion: this.ctx.pivot.quaternion.clone(),
      jawsPivotPosition: this.ctx.jaws.jawsPivot.position.clone(),
      fixed: captureState(this.ctx.jaws.fixed.mesh),
      fixedEdges: captureState(this.ctx.jaws.fixed.edges),
      movable: captureState(this.ctx.jaws.movable.mesh),
      movableEdges: captureState(this.ctx.jaws.movable.edges)
    };

    const revertState = (state?: { position: Vector3; scale: Vector3 }, mesh?: Mesh | LineSegments) => {
      if (!state || !mesh) return;
      mesh.position.copy(state.position);
      mesh.scale.copy(state.scale);
    };

    // Revert to initial state for animation
    this.ctx.pivot.position.copy(initialState.pivotPosition);
    this.ctx.pivot.quaternion.copy(initialState.pivotQuaternion);
    this.ctx.jaws.jawsPivot.position.copy(initialState.jawsPivotPosition);
    revertState(initialState.fixed, this.ctx.jaws.fixed.mesh);
    revertState(initialState.fixedEdges, this.ctx.jaws.fixed.edges);
    revertState(initialState.movable, this.ctx.jaws.movable.mesh);
    revertState(initialState.movableEdges, this.ctx.jaws.movable.edges);

    const lerpVector = (out: Vector3, from: Vector3, to: Vector3, alpha: number) => {
      out.set(
        MathUtils.lerp(from.x, to.x, alpha),
        MathUtils.lerp(from.y, to.y, alpha),
        MathUtils.lerp(from.z, to.z, alpha)
      );
    };

    const animateDuration = 350;
    const tweenData = { t: 0 };
    const tween = new Tween(tweenData)
      .to({ t: 1 }, animateDuration)
      .easing(Easing.Cubic.InOut)
      .onUpdate(() => {
        lerpVector(this.ctx.pivot.position, initialState.pivotPosition, targetState.pivotPosition, tweenData.t);
        this.ctx.pivot.quaternion.copy(initialState.pivotQuaternion);
        this.ctx.pivot.quaternion.slerp(targetState.pivotQuaternion, tweenData.t);
        lerpVector(
          this.ctx.jaws.jawsPivot.position,
          initialState.jawsPivotPosition,
          targetState.jawsPivotPosition,
          tweenData.t
        );

        if (initialState.fixed && targetState.fixed) {
          if (this.ctx.jaws.fixed.mesh) {
            lerpVector(
              this.ctx.jaws.fixed.mesh.position,
              initialState.fixed.position,
              targetState.fixed.position,
              tweenData.t
            );
            lerpVector(this.ctx.jaws.fixed.mesh.scale, initialState.fixed.scale, targetState.fixed.scale, tweenData.t);
          }
          if (this.ctx.jaws.fixed.edges) {
            lerpVector(
              this.ctx.jaws.fixed.edges.position,
              initialState.fixed.position,
              targetState.fixed.position,
              tweenData.t
            );
            lerpVector(this.ctx.jaws.fixed.edges.scale, initialState.fixed.scale, targetState.fixed.scale, tweenData.t);
          }
        }

        if (initialState.movable && targetState.movable) {
          if (this.ctx.jaws.movable.mesh) {
            lerpVector(
              this.ctx.jaws.movable.mesh.position,
              initialState.movable.position,
              targetState.movable.position,
              tweenData.t
            );
            lerpVector(
              this.ctx.jaws.movable.mesh.scale,
              initialState.movable.scale,
              targetState.movable.scale,
              tweenData.t
            );
          }
          if (this.ctx.jaws.movable.edges) {
            lerpVector(
              this.ctx.jaws.movable.edges.position,
              initialState.movable.position,
              targetState.movable.position,
              tweenData.t
            );
            lerpVector(
              this.ctx.jaws.movable.edges.scale,
              initialState.movable.scale,
              targetState.movable.scale,
              tweenData.t
            );
          }
        }
      })
      .onComplete(() => {
        this.ctx.pivot.position.copy(targetState.pivotPosition);
        this.ctx.pivot.quaternion.copy(targetState.pivotQuaternion);
        this.ctx.jaws.jawsPivot.position.copy(targetState.jawsPivotPosition);
        revertState(targetState.fixed, this.ctx.jaws.fixed.mesh);
        revertState(targetState.fixedEdges, this.ctx.jaws.fixed.edges);
        revertState(targetState.movable, this.ctx.jaws.movable.mesh);
        revertState(targetState.movableEdges, this.ctx.jaws.movable.edges);
        this.checkJawCollisions();
        this.ctx.refreshDimensionsHelper();
        this.jawsAnimationTween = undefined;
      })
      .start();

    this.jawsAnimationTween = tween;

    return { effectiveJawWall, effectiveJawGap, atMinGap };
  }

  // ── Jaws result (protobuf) ─────────────────────────────────────────────

  async addJawsResult(
    resultProtoUrl: string,
    onProgress?: (progress: JawsResultProgress) => void,
  ): Promise<void> {
    this.removeJawsResult();
    if (!resultProtoUrl) return;

    const report = (percent: number | null, message: string): void => {
      onProgress?.({ percent, message });
    };

    // Result protos reach hundreds of MB, so the download is the slow part.
    report(0, 'Downloading result...');
    const assembly = await fetchResultProto(
      this.ctx.session.http,
      resultProtoUrl,
      DOWNLOAD_PROGRESS_SHARE,
      (percent) => report(percent, 'Downloading result...'),
    );

    // decodeAssembly blocks the main thread, so let the browser paint the new
    // message before it starts.
    report(DOWNLOAD_PROGRESS_SHARE, 'Preparing model...');
    await nextPaint();

    const softJawsAssembly = decodeAssembly(assembly);

    const partCount = softJawsAssembly.parts.length;
    for (let i = 0; i < partCount; i++) {
      report(
        DOWNLOAD_PROGRESS_SHARE + Math.round((i / partCount) * (100 - DOWNLOAD_PROGRESS_SHARE)),
        `Building geometry ${i + 1}/${partCount}...`,
      );
      const obj = softJawsAssembly.parts[i];
      const group = new Group();
      group.name = obj.name;
      group.userData = { id: obj.id, pid: obj.parentId };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const faces: any = [];

      const cutOffAngle = 20;

      // Skip the EdgesGeometry overlay on the transformed part when proto-supplied
      // CAD wires are available (STEP upload) — overlaying tessellation-derived
      // edges on top of crisp CAD wires just doubles up and looks noisy. Mesh
      // uploads (PLY/STL) carry no wires, so the overlay stays on.
      const isTransformedPart = obj.name === 'part' || obj.name === 'model';
      const hasCadWires = (obj.wires ?? []).some((w) => (w.geometry?.length ?? 0) > 0);
      const skipFeatureEdges = isTransformedPart && hasCadWires;

      for (const face of obj.faces) {
        if (!face.vertices?.length || !face.indices?.length) continue;

        // Same worker-backed path the input assembly uses. This used to run
        // three's mergeVertices + EdgeSplitModifier inline on the main thread,
        // which are string-keyed per vertex — on a large transformed part that
        // froze the tab or exhausted the heap before the result could render.
        const geometry = await this.ctx.processFaceGeometry(
          new Float32Array(face.vertices),
          new Uint32Array(face.indices),
          cutOffAngle
        );

        const isJawPart = obj.name === 'jaw_left' || obj.name === 'jaw_right';
        const material = isJawPart
          ? new MeshPhysicalMaterial({
              color: 0xB0B0B0,
              side: DoubleSide,
              flatShading: true,
              metalness: 0.8,
              roughness: 0.3,
              envMap: this.ctx.envMap,
              envMapIntensity: 0.3,
            })
          : new MeshPhysicalMaterial({
              color: 0x4A90D9,
              side: DoubleSide,
              // This is the transformed part/model, the same kind of surface
              // as a regular input part: match PerFaceMaterial's defaults.
              flatShading: true,
              metalness: PART_METALNESS,
              roughness: PART_ROUGHNESS,
              clearcoat: PART_CLEARCOAT,
              reflectivity: PART_REFLECTIVITY,
              envMap: this.ctx.envMap,
              envMapIntensity: PART_ENVMAP_INTENSITY,
            });

        const meshobj = new Mesh(geometry, material);
        // Compute BVH asynchronously in worker
        this.ctx.computeBVHAsync(meshobj.geometry).catch(() => {});
        meshobj.name = meshobj.uuid;
        meshobj.userData = { id: obj.id, parentId: obj.parentId, originalColor: obj.color, faceId: face.id };

        faces.push(meshobj.uuid);

        group.add(meshobj);
        this.ctx.jaws.resources.push(meshobj);

        const triangleCount = (geometry.getIndex()?.count ?? 0) / 3;
        if (!skipFeatureEdges && triangleCount <= FEATURE_EDGES_MAX_TRIANGLES) {
          const edgeGeometry = new EdgesGeometry(geometry, 30);
          const edgeLines = new LineSegments(
            edgeGeometry,
            new LineBasicMaterial({
              color: isJawPart ? 0x666666 : 0x1a5276,
              transparent: true,
              opacity: 0.4,
            })
          );
          edgeLines.name = `${meshobj.name}_edges`;
          group.add(edgeLines);
          this.ctx.jaws.resources.push(edgeLines);
        }
      }

      const wirematerial = new LineMaterial({
        color: new Color('rgb(0,0,0)'),
        linewidth: 1,
        dashed: false,
        precision: 'highp',
        polygonOffset: true,
        polygonOffsetFactor: -2,
        clippingPlanes: this.ctx.clippingPlanes,
        resolution: new Vector2(
          this.ctx.canvasWidth,
          this.ctx.canvasHeight
        )
      });

      const wires: string[] = [];
      const wiresGroup = new Group();
      wiresGroup.name = 'wires';

      // Merge all wire segments into a single geometry for better performance
      const allSegmentPositions: number[] = [];
      for (const wireValues of obj.wires) {
        const wire = wireValues.geometry;
        if (!wire?.length) continue;
        for (let j = 0; j < wire.length - 3; j += 3) {
          const x0 = wire[j], y0 = wire[j + 1], z0 = wire[j + 2];
          const x1 = wire[j + 3], y1 = wire[j + 4], z1 = wire[j + 5];
          if (Math.abs(x0) > 1e38 || Math.abs(y0) > 1e38 || Math.abs(z0) > 1e38 ||
              Math.abs(x1) > 1e38 || Math.abs(y1) > 1e38 || Math.abs(z1) > 1e38) continue;
          allSegmentPositions.push(x0, y0, z0, x1, y1, z1);
        }
      }
      if (allSegmentPositions.length > 0) {
        const mergedWireGeometry = new LineSegmentsGeometry();
        mergedWireGeometry.setPositions(allSegmentPositions);
        const mergedLine = new LineSegments2(mergedWireGeometry, wirematerial);
        mergedLine.computeLineDistances();
        mergedLine.userData['line_type'] = 'AssemblyMeshLine';
        mergedLine.scale.set(1, 1, 1);
        mergedLine.name = mergedLine.uuid;
        wires.push(mergedLine.name);
        wiresGroup.add(mergedLine);
        this.ctx.jaws.resources.push(mergedLine);
      }
      group.add(wiresGroup);

      group.scale.setScalar(1);

      this.ctx.jaws.result.add(group);
    }
    // Reposition result group so its bottom sits on the grid (ground plane)
    this.ctx.jaws.result.updateMatrixWorld(true);
    const resultBox = new Box3().setFromObject(this.ctx.jaws.result);
    const groundZ = this.ctx.gridHelper.position.z;
    this.ctx.jaws.result.position.z += groundZ - resultBox.min.z;

    // Hide jaw preview boxes and input assembly, show results
    this.ctx.jaws.group.visible = false;
    this.ctx.group.visible = false;
    this.ctx.jaws.result.visible = true;
    report(100, 'Result ready');
  }

  removeJawsResult(): void {
    if (this.ctx.jaws.result.children.length > 0) {
      // Matched on having a geometry/material rather than on Mesh or Line2:
      // the feature-edge overlays are LineSegments, which is neither, so they
      // were only detached and leaked one EdgesGeometry plus one material per
      // face on every re-run.
      this.ctx.jaws.resources.forEach((resource) => {
        const geometry = (resource as Mesh).geometry;
        const material = (resource as Mesh).material;
        geometry?.dispose();
        if (Array.isArray(material)) material.forEach((m) => m.dispose());
        else material?.dispose();
        if (resource instanceof Object3D) {
          resource.removeFromParent();
        }
      });
      this.ctx.jaws.resources = [];
    }
    // Show jaw preview boxes and input assembly
    this.ctx.jaws.group.visible = true;
    this.ctx.group.visible = true;
    this.ctx.jaws.result.visible = false;
  }

  // ── Collision detection ────────────────────────────────────────────────

  checkJawCollisions(): { fixedJawIntersects: boolean; movableJawIntersects: boolean } {
    if (!this.ctx.jaws.fixed.mesh || !this.ctx.jaws.movable.mesh) {
      return { fixedJawIntersects: false, movableJawIntersects: false };
    }

    let fixedJawIntersects = false;
    let movableJawIntersects = false;

    // Refresh world transforms — pivot rotation / part offset / jaw scale
    // changes from updateJaws() may not be in matrixWorld yet.
    this.ctx.pivot.updateMatrixWorld(true);
    this.ctx.group.updateMatrixWorld(true);
    this.ctx.jaws.group.updateMatrixWorld(true);
    this.ctx.jaws.fixed.mesh.updateMatrixWorld(true);
    this.ctx.jaws.movable.mesh.updateMatrixWorld(true);

    const fixedJawGeometry = this.ctx.jaws.fixed.mesh.geometry;
    const movableJawGeometry = this.ctx.jaws.movable.mesh.geometry;
    const fixedJawWorld = this.ctx.jaws.fixed.mesh.matrixWorld;
    const movableJawWorld = this.ctx.jaws.movable.mesh.matrixWorld;

    // intersectsGeometry takes a matrix that maps the jaw into the child's
    // LOCAL space. Use child.matrixWorld^-1 (not group's) so child-local
    // transforms are honored.
    for (const rawObj of this.ctx.objects) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = rawObj as any;
      if (!obj.group) continue;

      obj.group.updateMatrixWorld(true);

      for (const child of obj.group.children) {
        if (!child.geometry || !child.geometry.boundsTree) continue;
        // Merged mesh or legacy per-face mesh.
        if (!child.userData['isMergedMesh'] && !child.userData['faceId']) continue;

        child.updateMatrixWorld(true);
        const childInverseMatrix = child.matrixWorld.clone().invert();
        const modelBounds = child.geometry.boundsTree;

        if (!fixedJawIntersects && fixedJawGeometry.boundsTree) {
          const jawToChild = childInverseMatrix.clone().multiply(fixedJawWorld);
          if (modelBounds.intersectsGeometry(fixedJawGeometry, jawToChild)) {
            fixedJawIntersects = true;
          }
        }

        if (!movableJawIntersects && movableJawGeometry.boundsTree) {
          const jawToChild = childInverseMatrix.clone().multiply(movableJawWorld);
          if (modelBounds.intersectsGeometry(movableJawGeometry, jawToChild)) {
            movableJawIntersects = true;
          }
        }

        if (fixedJawIntersects && movableJawIntersects) break;
      }
      if (fixedJawIntersects && movableJawIntersects) break;
    }

    // Jaws are axis-aligned, so setFromObject AABBs are tight here.
    const fixedJawBox = new Box3().setFromObject(this.ctx.jaws.fixed.mesh);
    const movableJawBox = new Box3().setFromObject(this.ctx.jaws.movable.mesh);
    if (movableJawBox.intersectsBox(fixedJawBox)) {
      movableJawIntersects = false;
      fixedJawIntersects = false;
    }

    this.updateJawColors(fixedJawIntersects, movableJawIntersects);
    this.jawsValid = fixedJawIntersects && movableJawIntersects;

    this.updateJawIntersectionHighlight();

    return { fixedJawIntersects, movableJawIntersects };
  }

  /**
   * Feed each part's `PerFaceMaterial` the world-to-jaw-local matrices; the
   * fragment shader paints pixels inside either jaw's unit cube.
   */
  private updateJawIntersectionHighlight(): void {
    const fixedJaw = this.ctx.jaws.fixed.mesh;
    const movableJaw = this.ctx.jaws.movable.mesh;
    if (!fixedJaw || !movableJaw) return;

    fixedJaw.updateMatrixWorld(true);
    movableJaw.updateMatrixWorld(true);
    const fixedInv = new Matrix4().copy(fixedJaw.matrixWorld).invert();
    const movableInv = new Matrix4().copy(movableJaw.matrixWorld).invert();

    for (const rawObj of this.ctx.objects) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const obj = rawObj as any;
      if (!obj.group) continue;
      for (const child of obj.group.children) {
        if (!child.userData?.['isMergedMesh']) continue;
        const perFaceMaterial = child.userData['perFaceMaterial'] as PerFaceMaterial | undefined;
        if (!perFaceMaterial) continue;
        perFaceMaterial.setJawHighlight(fixedInv, movableInv);
      }
    }
  }

  updateJawColors(fixedIntersects: boolean, movableIntersects: boolean): void {
    if (!this.ctx.jaws.fixed.mesh || !this.ctx.jaws.movable.mesh) return;

    // Green = intersecting (clamping), red = not.
    const fixedMaterial = this.ctx.jaws.fixed.mesh.material as MeshStandardMaterial;
    fixedMaterial.color.setHex(fixedIntersects ? 0x27ae60 : 0xe74c3c);
    if (this.ctx.jaws.fixed.edges) {
      const fixedEdgeMaterial = this.ctx.jaws.fixed.edges.material as LineBasicMaterial;
      fixedEdgeMaterial.color.setHex(fixedIntersects ? 0x1e8449 : 0xc0392b);
    }

    const movableMaterial = this.ctx.jaws.movable.mesh.material as MeshStandardMaterial;
    movableMaterial.color.setHex(movableIntersects ? 0x27ae60 : 0xe74c3c);
    if (this.ctx.jaws.movable.edges) {
      const movableEdgeMaterial = this.ctx.jaws.movable.edges.material as LineBasicMaterial;
      movableEdgeMaterial.color.setHex(movableIntersects ? 0x1e8449 : 0xc0392b);
    }
  }

  showMinGapIndicator(fixedInnerX: number, movableInnerX: number, centerY: number, centerZ: number): void {
    const canvasContainer = document.getElementById('canvas-container');
    if (!canvasContainer) return;

    // Create badge DOM element if needed
    if (!this.minGapBadge) {
      this.minGapBadge = document.createElement('div');
      this.minGapBadge.className = 'jaw-min-gap-badge';
      this.minGapBadge.style.opacity = '0';
      this.minGapBadge.style.display = 'none';
      this.minGapBadge.style.transform = 'translate(-9999px, -9999px)';
      canvasContainer.appendChild(this.minGapBadge);
    }

    // Update label with unit-aware value
    const sys = this.ctx.unitConversionService.unitSystem();
    const converted = this.ctx.unitConversionService.convert(JawsManager.MIN_JAW_GAP, 'length', sys);
    const unitLabel = this.ctx.unitConversionService.getUnitLabel('length', sys);
    this.minGapBadge.textContent = `Min ${parseFloat(converted.toFixed(4))}${unitLabel}`;

    // Create 3D line group
    if (this.minGapGroup) {
      this.ctx.scene.remove(this.minGapGroup);
      this.disposeMinGapGroup();
    }

    this.minGapGroup = new Group();
    this.minGapGroup.renderOrder = 9999;

    if (!this.minGapLineMaterial) {
      this.minGapLineMaterial = new LineMaterial({
        color: new Color(0xeab308),
        linewidth: 2,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
        depthWrite: false
      });
      this.minGapLineMaterial.resolution.set(
        this.ctx.canvasWidth,
        this.ctx.canvasHeight
      );
    }

    const extLen = 2;
    const p0 = new Vector3(fixedInnerX, centerY, centerZ);
    const p1 = new Vector3(movableInnerX, centerY, centerZ);
    this.minGapMidpoint.copy(p0).add(p1).multiplyScalar(0.5);

    // Main horizontal line
    this.minGapGroup.add(this.createMinGapLine([p0.x, p0.y, p0.z, p1.x, p1.y, p1.z]));

    // Vertical tick at fixed inner edge
    this.minGapGroup.add(this.createMinGapLine([
      p0.x, p0.y, p0.z - extLen,
      p0.x, p0.y, p0.z + extLen
    ]));

    // Vertical tick at movable inner edge
    this.minGapGroup.add(this.createMinGapLine([
      p1.x, p1.y, p1.z - extLen,
      p1.x, p1.y, p1.z + extLen
    ]));

    this.ctx.scene.add(this.minGapGroup);

    this.minGapBadge.style.display = 'flex';
    this.minGapBadge.classList.add('visible');
    this.minGapVisible = true;
  }

  hideMinGapIndicator(): void {
    if (this.minGapBadge) {
      this.minGapBadge.classList.remove('visible');
      this.minGapBadge.style.display = 'none';
    }
    if (this.minGapGroup) {
      this.ctx.scene.remove(this.minGapGroup);
      this.disposeMinGapGroup();
    }
    this.minGapVisible = false;
  }

  triggerMinGapPulse(): void {
    if (!this.minGapBadge) return;
    this.minGapBadge.classList.remove('pulse');
    // Force reflow to restart animation
    void this.minGapBadge.offsetWidth;
    this.minGapBadge.classList.add('pulse');
    if (this.pulseTimeout) clearTimeout(this.pulseTimeout);
    this.pulseTimeout = setTimeout(() => {
      this.minGapBadge?.classList.remove('pulse');
      this.pulseTimeout = undefined;
    }, 450);
  }

  updateMinGapBadgePosition(): void {
    if (!this.minGapVisible || !this.minGapBadge) return;

    const pos = this.minGapMidpoint.clone().project(this.ctx.camera);
    if (pos.z > 1) {
      this.minGapBadge.style.opacity = '0';
      return;
    }

    this.minGapBadge.style.opacity = '1';
    const el = this.ctx.renderer.domElement;
    const clientX = (el.offsetWidth * (pos.x + 1)) / 2 + el.offsetLeft;
    const clientY = (-el.offsetHeight * (pos.y - 1)) / 2 + el.offsetTop;
    const w = this.minGapBadge.offsetWidth;
    const h = this.minGapBadge.offsetHeight;
    const tx = clientX - w / 2;
    const ty = clientY - h - 8; // Position above the line
    this.minGapBadge.style.setProperty('--badge-translate', `translate(${tx}px, ${ty}px)`);
    this.minGapBadge.style.transform = `translate(${tx}px, ${ty}px)`;
  }

  private createMinGapLine(points: number[]): Line2 {
    const geometry = new LineGeometry();
    geometry.setPositions(points);
    const line = new Line2(geometry, this.minGapLineMaterial!);
    line.computeLineDistances();
    line.renderOrder = 9999;
    return line;
  }

  private disposeMinGapGroup(): void {
    if (!this.minGapGroup) return;
    this.minGapGroup.children.forEach((child: Object3D) => {
      if ((child as Line2).geometry) (child as Line2).geometry.dispose();
    });
    this.minGapGroup = undefined;
  }

  // ── Model rotation ─────────────────────────────────────────────────────

  rotateModelToFace(faceNormal: Vector3, onComplete: (modelRotation: { x: number; y: number; z: number }) => void): void {
    if (!this.ctx.session.project.softJaws) return;

    const startQuaternion = this.ctx.pivot.quaternion.clone();
    const targetDown = new Vector3(0, 0, -1);
    faceNormal.normalize();
    const tempQuaternion = new Quaternion();
    const rotationAxis = new Vector3().crossVectors(faceNormal, targetDown);

    if (rotationAxis.lengthSq() < 0.0001) {
      if (faceNormal.dot(targetDown) > 0) {
        tempQuaternion.identity();
      } else {
        tempQuaternion.setFromAxisAngle(new Vector3(1, 0, 0), Math.PI);
      }
    } else {
      const angle = Math.acos(Math.max(-1, Math.min(1, faceNormal.dot(targetDown))));
      rotationAxis.normalize();
      tempQuaternion.setFromAxisAngle(rotationAxis, angle);
    }

    // Animate rotation with tween
    const tweenData = { t: 0 };
    new Tween(tweenData)
      .to({ t: 1 }, 400) // 800ms animation duration
      .easing(Easing.Cubic.InOut)
      .onUpdate(() => {
        this.ctx.pivot.quaternion.copy(startQuaternion).slerp(tempQuaternion, tweenData.t);
      })
      .onComplete(() => {
        // 'ZYX' — NOT three.js' default 'XYZ'. Every consumer of modelRotation
        // (updateJaws' rotateOnWorldAxis X→Y→Z, the rotation gizmo's matrix
        // decomposition, and rotate_model.py's Rz*Ry*Rx) applies X first, which
        // composes to Rz*Ry*Rx. Decomposing as 'XYZ' yields angles for Rx*Ry*Rz,
        // so any face-align needing more than one axis snapped the part to a
        // different orientation than the one the user picked — and the run then
        // used those wrong angles.
        const euler = new Euler().setFromQuaternion(this.ctx.pivot.quaternion, 'ZYX');
        const modelRotation = {
          x: Math.round(MathUtils.radToDeg(euler.x) * 100) / 100,
          y: Math.round(MathUtils.radToDeg(euler.y) * 100) / 100,
          z: Math.round(MathUtils.radToDeg(euler.z) * 100) / 100
        };
        onComplete(modelRotation);
      })
      .start();
  }

  // ── Z-rotation arrows ─────────────────────────────────────────────────

  // ── Rotation gizmo ────────────────────────────────────────────────────

  /**
   * Enable rotation gizmo for soft jaws
   * @param onChange Callback function that receives rotation changes
   */
  enableRotationGizmo(onChange: (rotation: { x: number; y: number; z: number }) => void): void {
    if (this.ctx.session.project.projectType !== ProjectType.SoftJaws) return;

    // Create rotation controls if they don't exist
    if (!this.ctx.rotationControls) {
      this.ctx.rotationControls = new TransformControls(this.ctx.camera, this.ctx.renderer.domElement);
      this.ctx.rotationControls.setMode('rotate');
      this.ctx.rotationControls.setSpace('world');
      this.ctx.rotationControls.setSize(0.5);
      this.ctx.scene.add(this.ctx.rotationControls.getHelper());

      // Disable orbit controls when dragging the gizmo
      this.ctx.rotationControls.addEventListener('dragging-changed', (event) => {
        this.ctx.controls.enabled = !event.value;
        if (!event.value) this.onRotationDragEnd?.();
      });

      // Handle rotation change with smooth updates
      this.ctx.rotationControls.addEventListener('objectChange', () => {
        if (this.rotationChangeCallback && this.ctx.session.project.softJaws) {
          const currentQuat = this.ctx.pivot.quaternion.clone();
          const tempPivot = new Object3D();
          tempPivot.quaternion.copy(currentQuat);
          tempPivot.updateMatrix();

          const m = tempPivot.matrix.elements;
          let rX, rY, rZ;
          const sy = -m[2];
          rY = Math.asin(Math.max(-1, Math.min(1, sy)));
          const cy = Math.cos(rY);

          if (Math.abs(cy) > 0.0001) {
            rX = Math.atan2(m[6] / cy, m[10] / cy);
            rZ = Math.atan2(m[1] / cy, m[0] / cy);
          } else {
            rX = 0;
            rZ = Math.atan2(-m[4], m[5]);
          }

          this.rotationChangeCallback({
            x: Math.round(MathUtils.radToDeg(rX) * 100) / 100,
            y: Math.round(MathUtils.radToDeg(rY) * 100) / 100,
            z: Math.round(MathUtils.radToDeg(rZ) * 100) / 100
          });
        }
        // The editor redraws only on demand; the gizmo helper moves with the
        // pointer every tick of this drag and must not wait for the callback
        // above (a form patch) to eventually trigger updateJaws()'s tween.
        this.ctx.session.engine?.requestRender();
      });
    }

    // Attach to pivot and store callback
    this.ctx.rotationControls.attach(this.ctx.pivot);
    this.rotationChangeCallback = onChange;
    this.ctx.rotationControls.getHelper().visible = true;
  }

  /**
   * Disable rotation gizmo
   */
  disableRotationGizmo(): void {
    if (this.ctx.rotationControls) {
      this.ctx.rotationControls.detach();
      this.ctx.rotationControls.getHelper().visible = false;
      this.rotationChangeCallback = undefined;
      this.onRotationDragEnd = undefined;
      this.ctx.controls.enabled = true;
    }
  }

  /**
   * Update rotation gizmo visibility and attachment
   */
  updateRotationGizmo(): void {
    if (this.ctx.rotationControls && this.ctx.rotationControls.getHelper().visible) {
      // Ensure the gizmo stays attached to the pivot
      if (this.ctx.rotationControls.object !== this.ctx.pivot) {
        this.ctx.rotationControls.attach(this.ctx.pivot);
      }
    }
  }
}
